import React, { useState, useEffect } from 'react';
import { deviceAPI, assignmentAPI, employeeAPI, statsAPI, usersAPI, transferAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './DeviceList.css';

const Dashboard = ({ onViewChange }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [devices, setDevices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('myDevices');
  const [selectedDU, setSelectedDU] = useState(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [showAvailableOnly, setShowAvailableOnly] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (user) {
      loadPendingRequests();
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [statsRes, devicesRes, assignmentsRes, employeesRes, usersRes] = await Promise.all([
        statsAPI.getAll(),
        deviceAPI.getAll(),
        assignmentAPI.getAll(),
        employeeAPI.getAll(),
        usersAPI.getDeviceManagers()
      ]);
      
      console.log('Dashboard - Stats Response:', statsRes.data);
      console.log('Dashboard - statsByDeliveryUnit:', statsRes.data?.statsByDeliveryUnit);
      
      setStats(statsRes.data);
      setDevices(devicesRes.data);
      setAssignments(assignmentsRes.data);
      setEmployees(employeesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    try {
      // Load pending assignment requests assigned TO the current user
      console.log('Dashboard - Loading pending assignment requests for user:', user?.email, 'role:', user?.role);
      if (user?.email) {
        const response = await transferAPI.getMyTransfers();
        console.log('Dashboard - All assignment/transfer requests:', response.data);
        // Filter for pending requests where the current user is the recipient (toEmployeeEmail)
        const pendingRequestsToMe = response.data.filter(
          req => req.status === 'pending' && 
          req.toEmployeeEmail && 
          req.toEmployeeEmail.toLowerCase() === user.email.toLowerCase()
        );
        console.log('Dashboard - Pending assignment requests TO me count:', pendingRequestsToMe.length);
        console.log('Dashboard - Pending assignment requests TO me:', pendingRequestsToMe);
        setPendingRequestsCount(pendingRequestsToMe.length);
      } else {
        console.log('Dashboard - User email not available, skipping pending requests check');
      }
    } catch (error) {
      console.error('Failed to load pending assignment requests:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  if (!stats) {
    return <div className="dashboard-loading">Failed to load statistics</div>;
  }

  // Get all unique device types
  const getAllDeviceTypes = () => {
    const types = new Set();
    devices.forEach(d => {
      if (d.deviceType) types.add(d.deviceType);
    });
    return Array.from(types).sort();
  };

  const deviceTypes = getAllDeviceTypes();

  // Get user's assigned devices
  const getUserAssignedDevices = () => {
    if (!user) return [];
    
    const userAssignments = assignments.filter(a => 
      a.status === 'active' && 
      a.employeeId && 
      a.employeeId.toLowerCase() === (user.email || '').toLowerCase()
    );
    
    let assignedDevices = userAssignments.map(assignment => {
      const device = devices.find(d => d.assetId === assignment.deviceId);
      return device ? { ...device, assignmentId: assignment._id } : null;
    }).filter(d => d !== null);
    
    // Apply Good condition filter if enabled
    if (showAvailableOnly) {
      assignedDevices = assignedDevices.filter(device => 
        (device.condition || '').toLowerCase() === 'good'
      );
    }
    
    return assignedDevices;
  };

  const assignedDevices = getUserAssignedDevices();

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p style={{ 
            margin: '0.25rem 0 0 0', 
            color: '#6b7280', 
            fontSize: '0.875rem',
            fontWeight: '400'
          }}>
            Overview of your device fleet
          </p>
        </div>
      </div>

      {/* Overall Statistics Summary */}
      <div style={{ 
        backgroundColor: '#f0f9ff',
        padding: '1rem',
        marginBottom: '1.5rem',
        borderRadius: '8px',
        border: '2px solid #0284c7'
      }}>
        <h2 style={{ 
          margin: '0 0 0.75rem 0', 
          fontSize: '1rem', 
          fontWeight: '700', 
          color: '#0c4a6e',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.125rem' }}>📊</span>
          Overall Statistics
        </h2>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: user?.role === 'employee' ? 'repeat(4, 1fr)' : 'repeat(5, 1fr)',
          gap: '0.75rem'
        }}>
          <div style={{ 
            backgroundColor: '#ffffff',
            padding: '1rem',
            borderRadius: '6px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.375rem', fontWeight: '600' }}>
              TOTAL USERS
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#003E97' }}>
              {users.length}
            </div>
            {user?.role !== 'employee' && (
              <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                {users.filter(u => u.role === 'super_admin').length} Admin • {users.filter(u => u.role === 'device_manager').length} Manager • {users.filter(u => u.role === 'employee').length} Employee
              </div>
            )}
          </div>

          <div style={{ 
            backgroundColor: '#ffffff',
            padding: '1rem',
            borderRadius: '6px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.375rem', fontWeight: '600' }}>
              REGISTERED DEVICES
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#3b82f6' }}>
              {devices.length}
            </div>
          </div>

          <div style={{ 
            backgroundColor: '#ffffff',
            padding: '1rem',
            borderRadius: '6px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.375rem', fontWeight: '600' }}>
              ASSIGNED
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#10b981' }}>
              {assignments.filter(a => a.status === 'active').length}
            </div>
          </div>

          <div style={{ 
            backgroundColor: '#ffffff',
            padding: '1rem',
            borderRadius: '6px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.375rem', fontWeight: '600' }}>
              AVAILABLE (GOOD)
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#f59e0b' }}>
              {devices.filter(d => {
                const isAssigned = assignments.some(a => a.status === 'active' && a.deviceId === d.assetId);
                return !isAssigned && d.condition?.toLowerCase() === 'good';
              }).length}
            </div>
          </div>

          {user?.role !== 'employee' && (
            <div style={{ 
              backgroundColor: '#ffffff',
              padding: '1rem',
              borderRadius: '6px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}>
              <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.375rem', fontWeight: '600' }}>
                DELIVERY UNITS
              </div>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#8b5cf6' }}>
                {stats.statsByDeliveryUnit ? Object.keys(stats.statsByDeliveryUnit).length : 0}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        borderBottom: '2px solid #e5e7eb',
        marginBottom: '2rem'
      }}>
        <button
          onClick={() => setActiveTab('myDevices')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: activeTab === 'myDevices' ? '#003E97' : 'transparent',
            color: activeTab === 'myDevices' ? '#ffffff' : '#6b7280',
            border: 'none',
            borderBottom: activeTab === 'myDevices' ? '3px solid #003E97' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '0.9375rem',
            fontWeight: '600',
            transition: 'all 0.2s',
            marginBottom: '-2px'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'myDevices') {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'myDevices') {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          📱 My Devices
        </button>
        
        {user?.role !== 'employee' && (
          <button
            onClick={() => setActiveTab('duData')}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: activeTab === 'duData' ? '#003E97' : 'transparent',
              color: activeTab === 'duData' ? '#ffffff' : '#6b7280',
              border: 'none',
              borderBottom: activeTab === 'duData' ? '3px solid #003E97' : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '0.9375rem',
              fontWeight: '600',
              transition: 'all 0.2s',
              marginBottom: '-2px'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'duData') {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'duData') {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            🏢 Delivery Unit Statistics
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'myDevices' && (
        <div className="dashboard-content" style={{ padding: '0' }}>
          <div className="dashboard-section" style={{ padding: '0.75rem 1rem' }}>
            {/* Filter Bar */}
            <div style={{
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #003E97 0%, #1a5bb8 50%, #4A8FD8 100%)',
              borderRadius: '8px',
              marginBottom: '12px',
              boxShadow: '0 4px 6px -1px rgba(0, 62, 151, 0.2), 0 2px 4px -1px rgba(0, 62, 151, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#ffffff' }}>
                My Assigned Devices
                {user?.email && (
                  <span style={{ 
                    marginLeft: '1rem', 
                    fontSize: '0.75rem', 
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: '400'
                  }}>
                    (Pending requests: {pendingRequestsCount})
                  </span>
                )}
              </h3>
              
              {/* Toggle Slider for Good Condition Filter */}
              <div className="toggle-slider-container">
                <span className="toggle-slider-label">Good Condition</span>
                <div 
                  className={`toggle-slider ${!showAvailableOnly ? 'inactive' : ''}`}
                  onClick={() => setShowAvailableOnly(!showAvailableOnly)}
                  title="Filter devices with condition 'Good'"
                >
                  <div className="toggle-slider-thumb"></div>
                </div>
                <span className="toggle-slider-status">
                  {showAvailableOnly ? 'ON' : 'OFF'}
                </span>
              </div>
            </div>

            {/* Pending Assignment Requests Warning */}
            {pendingRequestsCount > 0 && (
              <div 
                style={{
                  marginBottom: '1rem',
                  padding: '0.875rem 1rem',
                  backgroundColor: '#fef3c7',
                  border: '2px solid #fbbf24',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 2px 4px rgba(251, 191, 36, 0.2)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight: '600', color: '#92400e', fontSize: '0.9375rem' }}>
                      {pendingRequestsCount} Pending Assignment Request{pendingRequestsCount > 1 ? 's' : ''}
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: '#78350f', marginTop: '0.125rem' }}>
                      Review and approve/reject device assignment requests
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => onViewChange('transfer-requests')}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#fbbf24',
                    color: '#78350f',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f59e0b';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#fbbf24';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  View Requests →
                </button>
              </div>
            )}

            {assignedDevices.length === 0 ? (
              <div style={{
                padding: '3rem',
                backgroundColor: '#f9fafb',
                borderRadius: '10px',
                color: '#6b7280',
                textAlign: 'center',
                border: '2px dashed #d1d5db'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                <p style={{ margin: 0, fontSize: '1.125rem', fontWeight: '500' }}>
                  No devices currently assigned to you
                </p>
              </div>
            ) : (
              <div className="table-container" style={{ 
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <table style={{ marginTop: '0', boxShadow: 'none', border: 'none' }}>
                  <thead>
                    <tr>
                      <th>Asset ID</th>
                      <th>DU</th>
                      <th>Device Type</th>
                      <th>Device Name</th>
                      <th>Model</th>
                      <th>OS</th>
                      <th>Condition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedDevices.map((device) => (
                      <tr key={device.assetId}>
                        <td style={{ fontWeight: '600', color: '#003E97' }}>
                          {device.assetId}
                        </td>
                        <td style={{ fontSize: '0.8125rem', color: '#374151' }}>
                          {device.deliveryUnit || 'N/A'}
                        </td>
                        <td>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: '#eff6ff',
                            color: '#1e40af',
                            borderRadius: '12px',
                            fontSize: '0.8125rem',
                            fontWeight: '600'
                          }}>
                            {device.deviceType || device.type || 'N/A'}
                          </span>
                        </td>
                        <td>{device.deviceName || 'N/A'}</td>
                        <td>{device.model || 'N/A'}</td>
                        <td style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                          {device.osVersion || 'N/A'}
                        </td>
                        <td>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: device.condition?.toLowerCase() === 'excellent' ? '#d1fae5' :
                                           device.condition?.toLowerCase() === 'good' ? '#a7f3d0' :
                                           device.condition?.toLowerCase() === 'fair' ? '#fef3c7' : '#fee2e2',
                            color: device.condition?.toLowerCase() === 'excellent' ? '#065f46' :
                                   device.condition?.toLowerCase() === 'good' ? '#047857' :
                                   device.condition?.toLowerCase() === 'fair' ? '#92400e' : '#991b1b',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            {device.condition || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'duData' && (
        <div className="dashboard-content" style={{ padding: '0' }}>
          <div className="dashboard-section" style={{ padding: '0.75rem 1rem' }}>
            <h3 style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: '600' }}>
              Delivery Unit Statistics
            </h3>
            {!stats.statsByDeliveryUnit ? (
              <div style={{
                padding: '2rem',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                color: '#92400e',
                textAlign: 'center'
              }}>
                <p>No delivery unit statistics available. Please restart the backend server to load the updated statistics.</p>
              </div>
            ) : Object.keys(stats.statsByDeliveryUnit).length === 0 ? (
              <div style={{
                padding: '2rem',
                backgroundColor: '#f3f4f6',
                borderRadius: '8px',
                color: '#6b7280',
                textAlign: 'center'
              }}>
                <p>No devices or employees found in any delivery unit.</p>
              </div>
            ) : (
              <div className="table-container" style={{ 
                overflowX: 'auto', 
                margin: '0',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <table style={{ 
                  fontSize: '0.875rem', 
                  marginTop: '0', 
                  boxShadow: 'none', 
                  border: 'none'
                }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: '120px', padding: '0.5rem 0.75rem', position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 10 }}>
                        DU Name
                      </th>
                      <th style={{ minWidth: '100px', padding: '0.5rem 0.75rem' }}>Employees</th>
                      <th style={{ minWidth: '100px', padding: '0.5rem 0.75rem' }}>Devices</th>
                      {deviceTypes.map(deviceType => (
                        <th key={deviceType} style={{ minWidth: '110px', padding: '0.5rem 0.75rem' }}>
                          {deviceType}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(stats.statsByDeliveryUnit)
                      .sort((a, b) => a[0].localeCompare(b[0]))
                      .map(([deliveryUnit, duStats]) => (
                        <React.Fragment key={deliveryUnit}>
                          <tr 
                            onClick={() => setSelectedDU(selectedDU === deliveryUnit ? null : deliveryUnit)}
                            style={{
                              cursor: 'pointer',
                              backgroundColor: selectedDU === deliveryUnit ? '#f0f9ff' : 'transparent',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedDU !== deliveryUnit) {
                                e.currentTarget.style.backgroundColor = '#f9fafb';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedDU !== deliveryUnit) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              } else {
                                e.currentTarget.style.backgroundColor = '#f0f9ff';
                              }
                            }}
                          >
                            <td style={{ 
                              fontWeight: '600', 
                              color: selectedDU === deliveryUnit ? '#0284c7' : '#003E97',
                              position: 'sticky',
                              left: 0,
                              backgroundColor: selectedDU === deliveryUnit ? '#f0f9ff' : '#fff',
                              zIndex: 5,
                              padding: '0.5rem 0.75rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ 
                                  fontSize: '0.875rem',
                                  transition: 'transform 0.2s',
                                  transform: selectedDU === deliveryUnit ? 'rotate(90deg)' : 'rotate(0deg)'
                                }}>
                                  ▶
                                </span>
                                {deliveryUnit}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: '600', color: '#1a5bb8', padding: '0.5rem 0.75rem' }}>
                              {duStats.totalEmployees}
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: '600', color: '#3b82f6', padding: '0.5rem 0.75rem' }}>
                              {duStats.totalDevices}
                            </td>
                            {deviceTypes.map(deviceType => {
                              const typeStats = duStats.deviceTypes?.[deviceType];
                              if (!typeStats) {
                                return (
                                  <td key={deviceType} style={{ textAlign: 'center', color: '#9ca3af', padding: '0.5rem 0.75rem' }}>
                                    -
                                  </td>
                                );
                              }
                              return (
                                <td key={deviceType} style={{ textAlign: 'center', padding: '0.5rem 0.75rem' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', alignItems: 'center' }}>
                                    <span style={{ 
                                      fontSize: '0.8125rem',
                                      fontWeight: '600',
                                      color: '#10b981'
                                    }}>
                                      {typeStats.assigned}
                                    </span>
                                    <span style={{ 
                                      fontSize: '0.7rem',
                                      color: '#6b7280'
                                    }}>
                                      ({typeStats.free} free)
                                    </span>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                          
                          {/* Expandable Detail Row */}
                          {selectedDU === deliveryUnit && (
                            <tr key={`${deliveryUnit}-detail`}>
                              <td 
                                colSpan={3 + deviceTypes.length} 
                                style={{ 
                                  padding: '0',
                                  backgroundColor: '#f0f9ff',
                                  borderTop: '2px solid #0284c7'
                                }}
                              >
                                <div style={{ 
                                  padding: '1rem',
                                  animation: 'fadeIn 0.3s ease-in-out'
                                }}>
                                  <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    marginBottom: '0.75rem'
                                  }}>
                                    <h4 style={{ 
                                      margin: 0, 
                                      fontSize: '0.9375rem', 
                                      fontWeight: '600', 
                                      color: '#0284c7' 
                                    }}>
                                      📊 Devices in {deliveryUnit}
                                    </h4>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedDU(null);
                                      }}
                                      style={{
                                        padding: '0.375rem 0.75rem',
                                        backgroundColor: '#ffffff',
                                        color: '#0284c7',
                                        border: '1px solid #0284c7',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '0.8125rem',
                                        fontWeight: '600',
                                        transition: 'all 0.2s'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#0284c7';
                                        e.currentTarget.style.color = '#ffffff';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#ffffff';
                                        e.currentTarget.style.color = '#0284c7';
                                      }}
                                    >
                                      ✕ Close
                                    </button>
                                  </div>

                                  {(() => {
                                    const duDevices = devices.filter(d => (d.deliveryUnit || 'Unassigned') === deliveryUnit);
                                    
                                    if (duDevices.length === 0) {
                                      return (
                                        <div style={{
                                          padding: '1.5rem',
                                          backgroundColor: '#ffffff',
                                          borderRadius: '6px',
                                          color: '#6b7280',
                                          textAlign: 'center'
                                        }}>
                                          No devices found in this delivery unit
                                        </div>
                                      );
                                    }

                                    // Aggregate devices by device type and model
                                    const deviceStats = {};
                                    duDevices.forEach(device => {
                                      const deviceType = device.deviceType || 'Unknown';
                                      const model = device.model || 'Unknown';
                                      const key = `${deviceType}|${model}`;
                                      
                                      if (!deviceStats[key]) {
                                        deviceStats[key] = {
                                          deviceType,
                                          model,
                                          assigned: 0,
                                          free: 0
                                        };
                                      }
                                      
                                      const isAssigned = assignments.some(a => 
                                        a.status === 'active' && a.deviceId === device.assetId
                                      );
                                      
                                      // Only count as free if not assigned and condition is Good
                                      const isGoodCondition = device.condition && device.condition.toLowerCase() === 'good';
                                      
                                      if (isAssigned) {
                                        deviceStats[key].assigned++;
                                      } else if (isGoodCondition) {
                                        deviceStats[key].free++;
                                      }
                                    });

                                    const statsArray = Object.values(deviceStats).sort((a, b) => {
                                      // Sort by Free count descending
                                      if (b.free !== a.free) {
                                        return b.free - a.free;
                                      }
                                      // If free count is same, sort by device type
                                      if (a.deviceType !== b.deviceType) {
                                        return a.deviceType.localeCompare(b.deviceType);
                                      }
                                      // If device type is same, sort by model
                                      return a.model.localeCompare(b.model);
                                    });

                                    return (
                                      <div style={{ 
                                        backgroundColor: '#ffffff',
                                        borderRadius: '6px',
                                        overflow: 'hidden',
                                        border: '1px solid #e5e7eb'
                                      }}>
                                        <div style={{ overflowX: 'auto' }}>
                                          <table style={{ 
                                            fontSize: '0.8125rem', 
                                            marginTop: '0', 
                                            boxShadow: 'none', 
                                            border: 'none',
                                            width: '100%'
                                          }}>
                                            <thead>
                                              <tr>
                                                <th style={{ padding: '0.5rem', fontSize: '0.8125rem' }}>Device Type</th>
                                                <th style={{ padding: '0.5rem', fontSize: '0.8125rem' }}>Model</th>
                                                <th style={{ padding: '0.5rem', fontSize: '0.8125rem', textAlign: 'center' }}>Assigned</th>
                                                <th style={{ padding: '0.5rem', fontSize: '0.8125rem', textAlign: 'center' }}>Free</th>
                                                <th style={{ padding: '0.5rem', fontSize: '0.8125rem', textAlign: 'center' }}>Total</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {statsArray.map((stat, index) => {
                                                const total = stat.assigned + stat.free;
                                                const utilizationPercent = total > 0 ? Math.round((stat.assigned / total) * 100) : 0;
                                                
                                                return (
                                                  <tr key={index}>
                                                    <td style={{ padding: '0.5rem' }}>
                                                      <span style={{
                                                        padding: '0.25rem 0.5rem',
                                                        backgroundColor: '#eff6ff',
                                                        color: '#1e40af',
                                                        borderRadius: '6px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: '600'
                                                      }}>
                                                        {stat.deviceType}
                                                      </span>
                                                    </td>
                                                    <td style={{ padding: '0.5rem', fontSize: '0.8125rem', fontWeight: '600', color: '#374151' }}>
                                                      {stat.model}
                                                    </td>
                                                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                                                      <span style={{
                                                        padding: '0.25rem 0.5rem',
                                                        backgroundColor: '#d1fae5',
                                                        color: '#065f46',
                                                        borderRadius: '6px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: '700'
                                                      }}>
                                                        {stat.assigned}
                                                      </span>
                                                    </td>
                                                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                                                      <span style={{
                                                        padding: '0.25rem 0.5rem',
                                                        backgroundColor: '#fef3c7',
                                                        color: '#92400e',
                                                        borderRadius: '6px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: '700'
                                                      }}>
                                                        {stat.free}
                                                      </span>
                                                    </td>
                                                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                                                        <span style={{ fontWeight: '700', color: '#3b82f6', fontSize: '0.8125rem' }}>
                                                          {total}
                                                        </span>
                                                        <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                                          ({utilizationPercent}% used)
                                                        </span>
                                                      </div>
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
